import { useState, useEffect, useCallback, useRef } from 'react';
import {
  EvaluationFilters,
  UseEvaluationsState,
  EvaluationRequest,
} from '../types';
import { apiService, APIError } from '../services/api';

export const useEvaluations = (initialFilters: EvaluationFilters = {}) => {
  const [state, setState] = useState<UseEvaluationsState>({
    evaluations: [],
    loading: false,
    error: null,
    total: 0,
    page: 1,
    pageSize: 20,
  });

  const [filters, setFilters] = useState<EvaluationFilters>(initialFilters);
  const debounceTimeoutRef = useRef<number | null>(null);

  const loadEvaluations = useCallback(
    async (
      page = state.page,
      pageSize = state.pageSize,
      currentFilters = filters
    ) => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const response = await apiService.getEvaluations(
          currentFilters,
          page,
          pageSize
        );
        setState(prev => ({
          ...prev,
          evaluations: response.data,
          total: response.total,
          page: response.page,
          pageSize: response.pageSize,
          loading: false,
        }));
      } catch (error) {
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al cargar evaluaciones');
        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
      }
    },
    [state.page, state.pageSize, filters]
  );

  const createEvaluation = useCallback(
    async (request: EvaluationRequest) => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        await apiService.createEvaluation(request);
        // Recargar la lista después de crear
        await loadEvaluations();
        return true;
      } catch (error) {
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al crear evaluación');
        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
        return false;
      }
    },
    [loadEvaluations]
  );

  const deleteEvaluation = useCallback(
    async (id: string) => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        await apiService.deleteEvaluation(id);
        // Recargar la lista después de eliminar
        await loadEvaluations();
        return true;
      } catch (error) {
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al eliminar evaluación');
        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
        return false;
      }
    },
    [loadEvaluations]
  );

  const updateFilters = useCallback(
    (newFilters: EvaluationFilters) => {
      setFilters(newFilters);

      // Limpiar timeout anterior si existe
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Implementar debouncing para evitar búsquedas constantes
      debounceTimeoutRef.current = window.setTimeout(() => {
        loadEvaluations(1, state.pageSize, newFilters);
        debounceTimeoutRef.current = null;
      }, 300); // 300ms de delay
    },
    [loadEvaluations, state.pageSize]
  );

  const setPage = useCallback(
    (page: number) => {
      setState(prev => ({ ...prev, page }));
      loadEvaluations(page);
    },
    [loadEvaluations]
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      setState(prev => ({ ...prev, pageSize, page: 1 }));
      loadEvaluations(1, pageSize);
    },
    [loadEvaluations]
  );

  const refreshEvaluations = useCallback(() => {
    loadEvaluations();
  }, [loadEvaluations]);

  const toggleLikeEvaluation = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const updatedEvaluation = await apiService.toggleLikeEvaluation(id);

      // Actualizar la evaluación en el estado local
      setState(prev => ({
        ...prev,
        evaluations: prev.evaluations.map(evaluation =>
          evaluation.id === id ? updatedEvaluation : evaluation
        ),
        loading: false,
      }));

      return updatedEvaluation;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al actualizar like');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return null;
    }
  }, []);

  const toggleTrainingData = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const updatedEvaluation = await apiService.toggleTrainingData(id);

      // Actualizar la evaluación en el estado local
      setState(prev => ({
        ...prev,
        evaluations: prev.evaluations.map(evaluation =>
          evaluation.id === id ? updatedEvaluation : evaluation
        ),
        loading: false,
      }));

      return updatedEvaluation;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al actualizar training data');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return null;
    }
  }, []);

  const exportEvaluations = useCallback(async () => {
    try {
      const blob = await apiService.exportEvaluations(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluaciones_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error:
          error instanceof APIError
            ? error
            : new APIError('Error al exportar evaluaciones'),
      }));
      return false;
    }
  }, [filters]);

  // Cargar datos iniciales
  useEffect(() => {
    loadEvaluations();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    ...state,
    filters,
    updateFilters,
    setPage,
    setPageSize,
    createEvaluation,
    deleteEvaluation,
    refreshEvaluations,
    exportEvaluations,
    toggleLikeEvaluation,
    toggleTrainingData,
  };
};
